import React, { useState } from "react";
import { useLang } from "@/lib/i18n";
import { Mail, ShieldCheck, X } from "lucide-react";

function LegalModal({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="legal-modal">
      <div className="rounded-2xl w-full max-w-lg p-8 max-h-[85vh] overflow-y-auto" style={{ background: "var(--at-card)", border: "1px solid var(--border-gold)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 className="font-serif-display font-bold text-2xl text-white">{item.title}</h3>
          <button onClick={onClose} data-testid="legal-modal-close" className="text-white/60 hover:text-white shrink-0"><X size={20} /></button>
        </div>
        <p className="text-sm font-body text-white/80 leading-relaxed whitespace-pre-line">{item.body}</p>
      </div>
    </div>
  );
}

export default function Footer({ settings }) {
  const { t } = useLang();
  const [legal, setLegal] = useState(null);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const legalContent = t("footer.legalContent");
  const legalLinks = t("footer.legalLinks");
  const legalKeys = ["terms", "disclaimer", "privacy", "faq"];

  return (
    <footer className="text-white/80 pt-16 pb-8" style={{ background: "var(--at-darkest)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="gold-rule mb-12" />
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-md overflow-hidden flex flex-col ring-1 ring-[var(--border-gold)]">
                <div className="flex-1 bg-white" /><div className="flex-1" style={{ background: "var(--austria-red)" }} /><div className="flex-1 bg-white" />
              </div>
              <div className="font-serif-display font-bold tracking-tight text-xl text-white">Austria<span className="gold-gradient-text"> Tips</span></div>
            </div>
            <p className="text-sm font-body text-white/65 leading-relaxed max-w-xs">{t("footer.tagline")}</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold font-heading px-3 py-1.5 rounded-full text-[#38040C]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}>
              <ShieldCheck size={13} /> {t("hero.badge")}
            </div>
            <a href="https://www.austriabet1x2.com" target="_blank" rel="noopener noreferrer" data-testid="footer-banner" className="block mt-5 w-[300px] max-w-full">
              <img src="/austria-banner.gif?v=2" alt="Österreichs Premium Fußball-Tipps" width={300} height={60} className="rounded-lg shadow-md ring-1 ring-[var(--border-gold)]" />
            </a>
          </div>

          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-4">{t("footer.links")}</h4>
            <ul className="space-y-2.5">
              {["performance", "about", "archive", "pricing"].map((id) => (
                <li key={id}><button data-testid={`footer-link-${id}`} onClick={() => scrollTo(id)} className="text-sm font-body text-white/65 hover:text-[var(--trust-gold)] transition-colors">{t(`nav.${id}`)}</button></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-4">{t("footer.legal")}</h4>
            <ul className="space-y-2.5">
              {legalKeys.map((k) => (
                <li key={k}><button data-testid={`footer-legal-${k}`} onClick={() => setLegal(legalContent[k])} className="text-sm font-body text-white/65 hover:text-[var(--trust-gold)] transition-colors">{legalLinks[k]}</button></li>
              ))}
            </ul>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mt-6 mb-3">{t("footer.contact")}</h4>
            <a href="mailto:markushuber@austriabet1x2.com" data-testid="footer-email" className="flex items-center gap-2.5 text-sm font-body text-white/65 hover:text-[var(--trust-gold)] transition-colors">
              <Mail size={16} /> markushuber@austriabet1x2.com
            </a>
            <div className="text-xs font-body text-white/45 mt-2">{t("footer.onlyMail")}</div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[var(--border-light)]">
          <p className="text-xs font-body text-white/50 leading-relaxed max-w-3xl">{t("footer.disclaimer")}</p>
          <div className="mt-4 text-xs font-body text-white/45">© 2023 Austria Tips · Markus Huber. {t("footer.rights")}</div>
        </div>
      </div>
      <LegalModal item={legal} onClose={() => setLegal(null)} />
    </footer>
  );
}
